"use client"
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@repo/web-ui/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

function ButtonAlt({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button">&
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean,
    color?: 'theme' | 'background',
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <div className="relative flex w-fit">
      <Comp
        data-slot="button"
        className={cn(
          buttonVariants({ size, className }),
          'shadow-none bg-transparent before:absolute before:left-0 before:top-0 before:h-full before:w-0 before:-z-10 before:rounded z-0 after:absolute after:bottom-0 after:left-4 after:w-5/6 after:h-1 after:z-10 after:rounded hover:bg-transparent hover:before:w-full hover:after:w-0 transition-colors duration-300 before:transition-all after:transition-all before:duration-300 after:duration-300 text-foreground hover:text-primary-foreground before:bg-primary after:bg-primary',
        )}
        {...props}
      />
    </div>
  )
}

export { Button, buttonVariants, ButtonAlt }
